import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ArrowRight,
  Sparkles,
  BookOpen,
  GraduationCap,
  CheckCircle2,
  HelpCircle,
  Award,
  Zap,
  Wallet,
  Infinity as InfinityIcon,
  ShieldCheck,
  Languages,
  Pencil,
  BarChart3,
  ChevronDown,
  Menu,
  X,
  Quote,
  Star,
  Play,
  Sun,
  Moon,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import logo from '../img/logo.png';
import boyImage from '../img/HiPageLogo.png';
import studentImg from '../img/landing/student.png';
import instructorImg from '../img/landing/instructor.png';
import graduationCap from '../img/landing/graduation_cap.png';
import sphere from '../img/landing/sphere.png';
import star from '../img/landing/star.png';
import tonus from '../img/landing/tonus.png';
import diamond from '../img/landing/diamond.png';
import chartCube from '../img/landing/chart_cuve.png';
import book from '../img/landing/book.png';

// Главный путь куда отправляем юзера на CTA-кнопках. Всё ведёт на /welcome
// (HiPage с формой логина/регистрации), как просил юзер. Не используем
// /auth — это легаси страница, на лендинге не нужна.
const AUTH_PATH = '/welcome';

// LandingPage
// ────────────
// Single-page лендинг с anchor-секциями вместо многостраничного сайта —
// это стандарт для современных SaaS (Notion, Linear, Vercel). Юзер видит
// всё сразу, нет лишних кликов, лучше для конверсии и SEO.
//
// Принципы:
//  • Sticky-хедер с anchor-навигацией + переключателем языка + кнопками «Войти/Начать»
//  • Hero с заголовком и иллюстрацией из плавающих UI-карточек (стиль картинки
//    юзера — без зависимости от внешнего файла)
//  • Stats — count-up анимация при появлении в viewport
//  • Секции «Студентам» / «Преподавателям» / «Технологии» с иконками
//  • «Как это работает» — 3 шага
//  • FAQ — accordion
//  • Footer со ссылками на legal-документы (уже у нас есть)
//
// Анимации: IntersectionObserver-based fade-up без сторонних библиотек.
// i18n: полный RU/KK через useTranslation. Переключение языка — мгновенно.

// ─── small hook: animate on scroll ──────────────────────────────────
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((s) => s.auth);
  const { theme, toggleTheme } = useTheme();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Header меняет стиль при скролле (бэкграунд + тень).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hash-scroll при кросс-роутовой навигации (например, с /welcome на
  // /#become-instructor). Браузер native-scroll не срабатывает потому,
  // что в момент загрузки секция ещё не отрисована React'ом — поэтому
  // ждём пару кадров через requestAnimationFrame и скроллим вручную.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Двойной rAF + небольшой fallback timeout — гарантируем что DOM
    // и Reveal-обёртки отрисованы до scroll.
    const id1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    return () => cancelAnimationFrame(id1);
  }, [location.hash]);

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem('i18nextLng', lng);
    } catch {
      /* ignore */
    }
  };

  // Если юзер залогинен — кнопка ведёт на дашборд по роли.
  // Если не залогинен — на /welcome (HiPage, форма входа/регистрации).
  const startPath = user
    ? user.roles?.includes('admin')
      ? '/dashboard/admin'
      : user.roles?.includes('instructor')
      ? '/dashboard/instructor'
      : '/dashboard/student'
    : AUTH_PATH;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white overflow-x-hidden">
      {/* ─────────────── HEADER ─────────────── */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all ${
          scrolled
            ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200/60 dark:border-slate-800/60 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={logo}
              alt="Uaqyt"
              className="h-[150px] w-[150px] object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 text-sm">
            <NavLink href="#students">{t('landing.navStudents')}</NavLink>
            <NavLink href="#instructors">{t('landing.navInstructors')}</NavLink>
            <NavLink href="#become-instructor">{t('landing.navTeach')}</NavLink>
            <NavLink href="#how">{t('landing.navHow')}</NavLink>
            <NavLink href="#faq">{t('landing.navFaq')}</NavLink>
          </nav>

          <div className="flex items-center gap-2">
            {/* Theme toggle — мобиль/десктоп оба видят, потому что это
                визуальный комфорт, не настройка аккаунта. */}
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              aria-label="Тема"
              title={theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            </button>

            {/* Lang switch */}
            <div className="hidden sm:inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-0.5">
              {[
                { id: 'ru', label: 'RU' },
                { id: 'kk', label: 'KK' },
              ].map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => changeLang(l.id)}
                  className={`px-2 py-1 text-xs font-semibold rounded-md transition ${
                    i18n.resolvedLanguage === l.id
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <Link
              to={AUTH_PATH}
              className="hidden sm:inline-flex items-center text-sm font-medium px-3 py-2 text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition"
            >
              {t('landing.navLogin')}
            </Link>

            <Link
              to={startPath}
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-2 text-sm font-semibold shadow-md shadow-indigo-500/20 transition"
            >
              {t('landing.navStart')}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>

            <button
              type="button"
              onClick={() => setMobileOpen((p) => !p)}
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
              aria-label="menu"
            >
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {[
                ['#students', t('landing.navStudents')],
                ['#instructors', t('landing.navInstructors')],
                ['#become-instructor', t('landing.navTeach')],
                ['#how', t('landing.navHow')],
                ['#faq', t('landing.navFaq')],
              ].map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  {label}
                </a>
              ))}
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => changeLang('ru')}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold ${
                    i18n.resolvedLanguage === 'ru'
                      ? 'bg-indigo-600 text-white'
                      : 'border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  Русский
                </button>
                <button
                  type="button"
                  onClick={() => changeLang('kk')}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold ${
                    i18n.resolvedLanguage === 'kk'
                      ? 'bg-indigo-600 text-white'
                      : 'border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  Қазақша
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="pt-20">
        {/* ─────────────── HERO ─────────────── */}
        <Hero t={t} startPath={startPath} />

        {/* ─────────────── BENEFITS (id=features) ───────────────
            Простая секция «почему Uaqyt» — 4 value-prop карточки
            (быстро / удобно / на казахском / навсегда). Без громких
            технических заявлений — только то, что реально есть. */}
        <Benefits t={t} />

        {/* ─────────────── TESTIMONIALS ─────────────── */}
        <Testimonials t={t} />

        {/* ─────────────── STUDENTS ─────────────── */}
        <Section id="students" tone="white">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
            {/* Image first on desktop, hidden on mobile (тяжёлая 3D-PNG) */}
            <Reveal className="hidden md:block">
              <div className="relative">
                {/* Soft blob behind image */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 dark:from-indigo-500/15 dark:to-purple-500/15 blur-3xl rounded-full" />
                <img
                  src={studentImg}
                  alt=""
                  aria-hidden="true"
                  className="relative w-full max-w-md mx-auto drop-shadow-2xl animate-float-slow"
                />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <SectionTitle>{t('landing.studentsTitle')}</SectionTitle>
              </Reveal>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {[
                  [Play, 'studentsItem1', 'indigo'],
                  [Award, 'studentsItem2', 'emerald'],
                  [Sparkles, 'studentsItem3', 'rose'],
                  [InfinityIcon, 'studentsItem4', 'blue'],
                ].map(([Icon, key, color], i) => (
                  <Reveal key={key} delay={i * 80}>
                    <FeatureCard
                      icon={Icon}
                      color={color}
                      title={t(`landing.${key}Title`)}
                      desc={t(`landing.${key}Desc`)}
                    />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ─────────────── INSTRUCTORS ─────────────── */}
        <Section id="instructors" tone="slate">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
            <div>
              <Reveal>
                <SectionTitle>{t('landing.instructorsTitle')}</SectionTitle>
              </Reveal>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {[
                  [Pencil, 'instructorsItem1', 'indigo'],
                  [Zap, 'instructorsItem2', 'amber'],
                  [BarChart3, 'instructorsItem3', 'emerald'],
                  [ShieldCheck, 'instructorsItem4', 'rose'],
                ].map(([Icon, key, color], i) => (
                  <Reveal key={key} delay={i * 80}>
                    <FeatureCard
                      icon={Icon}
                      color={color}
                      title={t(`landing.${key}Title`)}
                      desc={t(`landing.${key}Desc`)}
                    />
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal className="hidden md:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-200/40 to-cyan-200/40 dark:from-emerald-500/15 dark:to-cyan-500/15 blur-3xl rounded-full" />
                <img
                  src={instructorImg}
                  alt=""
                  aria-hidden="true"
                  className="relative w-full max-w-md mx-auto drop-shadow-2xl animate-float-slow"
                  style={{ animationDelay: '1s' }}
                />
              </div>
            </Reveal>
          </div>
        </Section>

        {/* ─────────────── BECOME INSTRUCTOR (WhatsApp CTA) ───────────────
            Идёт сразу после блока «что получит преподаватель» — естественный
            следующий шаг. Без формы: одна большая WhatsApp-кнопка ведёт
            на wa.me/77756199396 с предзаполненным сообщением. */}
        <BecomeInstructor t={t} />

        {/* ─────────────── HOW IT WORKS ─────────────── */}
        <Section id="how" tone="gradient">
          <Reveal>
            <SectionTitle>{t('landing.howTitle')}</SectionTitle>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-5 relative">
            {[1, 2, 3].map((step, i) => (
              <Reveal key={step} delay={i * 100}>
                <div className="relative h-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm">
                  <div className="absolute -top-4 left-6 inline-flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white font-bold text-sm shadow-md">
                    {step}
                  </div>
                  <h3 className="mt-2 text-lg font-bold text-slate-900 dark:text-white">
                    {t(`landing.howStep${step}Title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    {t(`landing.howStep${step}Desc`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* ─────────────── FAQ ─────────────── */}
        <Section id="faq" tone="white">
          <Reveal>
            <SectionTitle>{t('landing.faqTitle')}</SectionTitle>
          </Reveal>
          <div className="mt-8 max-w-3xl mx-auto space-y-3">
            {/* faq3 (про возврат) убран — пользователь увидит политику возврата
                в /legal/refund если потребуется. */}
            {[1, 2, 4, 5].map((n) => (
              <Reveal key={n} delay={n * 60}>
                <FaqItem
                  q={t(`landing.faq${n}Q`)}
                  a={t(`landing.faq${n}A`)}
                />
              </Reveal>
            ))}
          </div>
        </Section>

        {/* ─────────────── BOTTOM CTA ─────────────── */}
        <Section tone="cta">
          <Reveal>
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {t('landing.ctaTitle')}
              </h2>
              <p className="mt-3 text-base sm:text-lg text-white/85">
                {t('landing.ctaSubtitle')}
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={startPath}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-indigo-700 hover:bg-indigo-50 px-7 py-3.5 text-base font-bold shadow-2xl transition"
                >
                  {t('landing.ctaButton')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to={AUTH_PATH}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white/10 hover:bg-white/20 text-white border border-white/20 px-7 py-3.5 text-base font-semibold backdrop-blur"
                >
                  {t('landing.ctaSecondary')}
                </Link>
              </div>
            </div>
          </Reveal>
        </Section>

        {/* ─────────────── FOOTER ─────────────── */}
        <Footer t={t} navigate={navigate} />
      </main>
    </div>
  );
};

// ─── HERO ───────────────────────────────────────────────────────────
const Hero = ({ t, startPath }) => (
  <section className="relative overflow-hidden">
    {/* Soft gradient bg + blur blobs */}
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-950 dark:to-indigo-950/40" />
    <div className="absolute top-20 -left-32 h-96 w-96 rounded-full bg-indigo-300/30 dark:bg-indigo-500/15 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-20 -right-32 h-96 w-96 rounded-full bg-purple-300/30 dark:bg-purple-500/15 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20 lg:pt-20 lg:pb-28">
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
        {/* Text */}
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900 dark:text-white">
            <Trans
              i18nKey="landing.heroTitle"
              components={{
                1: (
                  <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent" />
                ),
              }}
            />
          </h1>

          <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl">
            {t('landing.heroSubtitle')}
          </p>

          {/* Mobile: CTAs слева + мальчик справа. На lg+ блок мальчика
              исчезает, кнопки разворачиваются на flex-wrap как раньше. */}
          <div className="mt-8 flex items-end justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Mobile: кнопки stack'аются вертикально на всю ширину контейнера —
                  иначе они зажимаются картинкой мальчика справа. Desktop:
                  inline как прежде (flex-row), без растягивания. */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                <Link
                  to={startPath}
                  className="inline-flex items-center justify-center sm:justify-start gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3.5 text-base font-bold shadow-xl shadow-indigo-500/30 transition w-full sm:w-auto"
                >
                  {/* size={20} + shrink-0 нужны вместе: iOS Safari иногда
                      игнорирует tailwind w-5/h-5 на lucide SVG (рендерит в
                      минимальный intrinsic-size), и без shrink-0 flex-родитель
                      сжимает иконку до нуля при узкой кнопке. */}
                  <BookOpen size={20} className="shrink-0" />
                  {t('landing.heroCtaStart')}
                </Link>
                <a
                  href="#become-instructor"
                  className="inline-flex items-center justify-center sm:justify-start gap-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 px-6 py-3.5 text-base font-semibold text-slate-800 dark:text-slate-200 transition w-full sm:w-auto"
                >
                  <Pencil size={20} className="shrink-0" />
                  {t('landing.heroCtaTeach')}
                </a>
              </div>

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                {t('landing.heroNoCard')}
              </p>
            </div>

            {/* Мальчик — справа от кнопок, прозрачный PNG. Только мобиль/планшет. */}
            <img
              src={boyImage}
              alt=""
              aria-hidden="true"
              className="lg:hidden shrink-0 h-64 sm:h-120 md:h-80 w-auto object-contain pointer-events-none select-none -mr-4 sm:-mr-6"
              style={{ filter: 'drop-shadow(0 20px 35px rgba(99,102,241,0.25))' }}
            />
          </div>
        </div>

        {/* Анимированная hero-композиция. Никаких внешних картинок — всё
            на CSS + SVG. Несколько слоёв с разными анимациями:
              • blob — пульсирующие градиентные капли в фоне
              • mock-карточки уроков/прогресса/квиза/сертификата
              • каждая floats со своим delay → выглядит «живо», но без
                раздражающего синхронного движения
            Иммобильно скрыто на mobile/tablet — там вместо неё мальчик. */}
        <HeroShowcase />
      </div>
    </div>
  </section>
);

// HeroShowcase — анимированный mockup дашборда + 3D-элементы вокруг.
// overflow-hidden обязателен — без него дашборд (большой 460px) может
// вылезать за пределы колонки на узких разрешениях.
const HeroShowcase = () => (
  <div className="relative hidden lg:block h-[600px] w-full overflow-visible">
    {/* Blob bg — три пульсирующие капли разных цветов */}
    <div className="absolute top-12 left-12 h-56 w-56 rounded-full bg-indigo-300/40 dark:bg-indigo-500/20 blur-3xl animate-blob" />
    <div className="absolute top-32 right-8 h-64 w-64 rounded-full bg-purple-300/40 dark:bg-purple-500/20 blur-3xl animate-blob" style={{ animationDelay: '4s' }} />
    <div className="absolute bottom-8 left-20 h-48 w-48 rounded-full bg-pink-300/30 dark:bg-pink-500/15 blur-3xl animate-blob" style={{ animationDelay: '8s' }} />

    {/* ─── 3D объекты — расставлены ПО ПЕРИМЕТРУ контейнера, не на
        центральный мок. Размеры увеличены, drop-shadow для глубины.
        z-0 → за моками, чтобы они оставались главными. */}
    {/* ─── 3D объекты — крупные, расставлены по периметру как раньше.
        Используем отрицательные смещения чтобы выходить за пределы
        колонки и создавать ощущение 3D-сцены. z-0 → за дашбордом.
        Дашборд w-[min(96%,460px)] поэтому даже на узких экранах есть
        пространство для 3D-объектов по бокам. */}
    {/* Шапка магистра — верх-лево, крупная, наклон */}
    <img
      src={graduationCap}
      alt=""
      aria-hidden="true"
      className="absolute -top-2 -left-8 w-40 animate-float-slow drop-shadow-2xl pointer-events-none z-0 -rotate-12"
      style={{ animationDelay: '0.5s' }}
    />
    {/* Звезда — верх-право */}
    <img
      src={star}
      alt=""
      aria-hidden="true"
      className="absolute top-2 -right-4 w-28 animate-float drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '1.2s' }}
    />
    {/* Торус — лево-середина */}
    <img
      src={tonus}
      alt=""
      aria-hidden="true"
      className="absolute top-1/2 -left-16 w-36 animate-float-slow drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '2s' }}
    />
    {/* Бриллиант — право-середина */}
    <img
      src={diamond}
      alt=""
      aria-hidden="true"
      className="absolute top-2/3 -right-12 w-32 animate-float drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '2.8s' }}
    />
    {/* Книга — низ-середина */}
    <img
      src={book}
      alt=""
      aria-hidden="true"
      className="absolute -bottom-6 right-1/3 w-40 animate-float-slow drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '0.3s' }}
    />
    {/* Сфера — низ-лево */}
    <img
      src={sphere}
      alt=""
      aria-hidden="true"
      className="absolute -bottom-2 -left-4 w-28 animate-float drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '1.7s' }}
    />
    {/* Чарт-куб — близ верха-середины, между шапкой и звездой */}
    <img
      src={chartCube}
      alt=""
      aria-hidden="true"
      className="absolute top-1/3 -right-2 w-24 animate-float drop-shadow-2xl pointer-events-none z-0"
      style={{ animationDelay: '3.2s' }}
    />

    {/* ─── НАСТОЯЩИЙ МОКАП ДАШБОРДА (как на референсе с Аружан) ─────
        Sidebar слева + main content справа. inset-x-0 + max-w + mx-auto
        вместо left-1/2/translate — гарантированно вмещается в колонку
        даже если она узкая. z-10 → поверх 3D-объектов фона. */}
    <div
      className="absolute top-6 inset-x-0 mx-auto w-[min(96%,460px)] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-float-slow z-10"
      style={{ animationDelay: '0s' }}
    >
      <div className="flex h-[400px]">
        {/* SIDEBAR — узкий, чтобы основной контент влез */}
        <div className="w-[110px] shrink-0 bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col py-2 px-1.5 gap-0.5">
          <div className="flex items-center gap-1.5 px-2 py-2 mb-2">
            <span className="h-5 w-5 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 inline-flex items-center justify-center">
              <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />
            </span>
            <span className="text-[11px] font-bold text-slate-900 dark:text-white">UAQYT</span>
          </div>

          {[
            { label: 'Главная', icon: '🏠', active: true },
            { label: 'Мои курсы', icon: '📚' },
            { label: 'Квизы', icon: '❓' },
            { label: 'Задания', icon: '📋' },
            { label: 'Результаты', icon: '📊' },
            { label: 'Сообщения', icon: '💬', badge: 3 },
            { label: 'Сертификаты', icon: '🏆' },
            { label: 'Настройки', icon: '⚙️' },
          ].map((it, i) => (
            <div
              key={i}
              className={`flex items-center justify-between gap-1.5 rounded-md px-2 py-1.5 text-[10px] font-medium ${
                it.active
                  ? 'bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              <span className="inline-flex items-center gap-1.5 min-w-0">
                <span className="text-xs leading-none">{it.icon}</span>
                <span className="truncate">{it.label}</span>
              </span>
              {it.badge && (
                <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-rose-500 text-white text-[8px] font-bold">
                  {it.badge}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* MAIN CONTENT */}
        <div className="flex-1 p-3 overflow-hidden">
          {/* Top bar: greeting + search + bell + avatar */}
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="min-w-0">
              <div className="text-[12px] font-bold text-slate-900 dark:text-white truncate">
                Привет, Аружан! 👋
              </div>
              <div className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                Продолжайте обучение и достигайте целей
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="h-5 w-28 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center px-1.5">
                <span className="text-[8px] text-slate-400">🔍 Поиск курсов...</span>
              </div>
              <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 text-[10px]">
                🔔
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 text-white text-[7px] font-bold flex items-center justify-center">7</span>
              </span>
              <span className="h-5 w-5 rounded-full bg-gradient-to-br from-pink-400 to-rose-500" />
            </div>
          </div>

          {/* Row 1: Progress + Quiz */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
                Мой прогресс
              </div>
              <div className="flex items-end gap-2">
                <div className="relative h-12 w-12 shrink-0">
                  <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="rgb(226,232,240)" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15"
                      fill="none"
                      stroke="rgb(99,102,241)"
                      strokeWidth="3"
                      strokeDasharray="68 94"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-900 dark:text-white">
                    72%
                  </div>
                </div>
                <div className="text-[9px] leading-tight">
                  <div className="font-bold text-slate-900 dark:text-white">3 из 12</div>
                  <div className="text-slate-500">уроков пройдено</div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
              <div className="flex items-center gap-1 mb-1">
                <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-[8px]">✓</span>
                <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400">Квиз сдан</span>
              </div>
              <div className="text-lg font-bold text-emerald-600 leading-none tabular-nums">85%</div>
              <div className="text-[8px] text-slate-500 mt-1">Следующий урок открыт</div>
            </div>
          </div>

          {/* Continue learning */}
          <div className="mb-2">
            <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
              Продолжить обучение
            </div>
            <div className="flex gap-2 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="w-16 h-14 shrink-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500 flex items-center justify-center">
                <span className="text-white font-bold text-[10px]">Python</span>
              </div>
              <div className="flex-1 py-1.5 pr-2 min-w-0">
                <div className="text-[10px] font-bold text-slate-900 dark:text-white truncate">
                  Python для начинающих
                </div>
                <div className="text-[8px] text-slate-500 mb-1">Урок 4 из 12 · 72%</div>
                <div className="h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div className="h-full rounded-full bg-indigo-500" style={{ width: '72%' }} />
                </div>
              </div>
              <button className="self-center mr-1.5 h-6 w-6 rounded-full bg-indigo-600 inline-flex items-center justify-center shrink-0">
                <Play className="w-3 h-3 text-white fill-white" />
              </button>
            </div>
          </div>

          {/* Row 2: Online + Top students */}
          <div className="grid grid-cols-[1fr_1.2fr] gap-2">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                Студентов онлайн
              </div>
              <div className="flex -space-x-1.5">
                {['from-indigo-400 to-blue-500', 'from-purple-400 to-pink-500', 'from-emerald-400 to-cyan-500', 'from-amber-400 to-rose-500'].map((g, i) => (
                  <span key={i} className={`h-4 w-4 rounded-full bg-gradient-to-br ${g} ring-1 ring-white dark:ring-slate-900`} />
                ))}
                <span className="h-4 w-4 rounded-full bg-slate-100 dark:bg-slate-800 ring-1 ring-white dark:ring-slate-900 flex items-center justify-center text-[7px] font-bold text-slate-600">+42</span>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                Топ студентов
              </div>
              <div className="space-y-0.5">
                {[
                  { name: 'Аружан', xp: '12 560', color: 'from-pink-400 to-rose-500' },
                  { name: 'Ерлан', xp: '9 430', color: 'from-indigo-400 to-blue-500' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className={`h-3 w-3 rounded-full bg-gradient-to-br ${s.color}`} />
                    <span className="text-[8px] font-medium text-slate-700 dark:text-slate-300 truncate flex-1">{s.name}</span>
                    <span className="text-[8px] font-bold text-amber-600 tabular-nums">{s.xp} XP</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Floating-карточка «Сертификат» — нижний правый угол как акцент.
        Маленькая, чтобы не загораживать главный мокап. */}
    <div
      className="absolute bottom-12 right-4 w-36 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border border-amber-200 dark:border-amber-900/40 shadow-2xl p-2.5 animate-float-slow z-20"
      style={{ animationDelay: '2.2s' }}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 shadow-md">
          <Award className="w-3 h-3 text-white" />
        </span>
        <span className="text-[9px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
          Сертификат
        </span>
      </div>
      <div className="h-1.5 w-3/4 rounded-full bg-amber-200/60 dark:bg-amber-700/40 mb-1" />
      <div className="h-1.5 w-1/2 rounded-full bg-amber-200/60 dark:bg-amber-700/40" />
    </div>

    {/* Аватары и mini-chart убраны — было слишком много карточек, сцена
        наслаивалась. Оставили: центральный плеер + квиз + сертификат —
        три ярких фокуса. */}
  </div>
);

// ─── BENEFITS (id="features") ───────────────────────────────────────
// 4 простых преимущества: быстро / удобно / двуязычно / навсегда.
// Без технических заявлений — то что реально работает у нас.
// Каждая карточка — gradient-border (через padding на gradient wrapper)
// + большая цветная иконка + hover lift. Реализация без CSS-mask,
// чтобы выглядело хорошо в обоих темах.
const BENEFIT_CARDS = [
  { icon: Zap,         color: 'amber',   from: 'from-amber-400',  to: 'to-orange-500' },
  { icon: CheckCircle2, color: 'emerald', from: 'from-emerald-400', to: 'to-cyan-500' },
  { icon: Languages,   color: 'indigo',  from: 'from-indigo-400',  to: 'to-purple-500' },
  { icon: InfinityIcon, color: 'rose',   from: 'from-rose-400',    to: 'to-pink-500' },
];

const Benefits = ({ t }) => (
  <section
    id="features"
    className="relative scroll-mt-20 bg-white dark:bg-slate-900 py-16 sm:py-24 overflow-hidden"
  >
    {/* Тонкие декоративные blob'ы на фоне — не отвлекают, но добавляют глубины */}
    <div className="absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 right-1/4 h-72 w-72 rounded-full bg-purple-200/30 dark:bg-purple-500/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t('landing.featuresTitle')}
          </h2>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {BENEFIT_CARDS.map((b, i) => (
          <Reveal key={i} delay={i * 80}>
            <BenefitCard
              icon={b.icon}
              from={b.from}
              to={b.to}
              title={t(`landing.feature${i + 1}Title`)}
              desc={t(`landing.feature${i + 1}Desc`)}
            />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const BenefitCard = ({ icon: Icon, from, to, title, desc }) => (
  <div className="group relative h-full">
    {/* Gradient «свечение» под карточкой — появляется на hover */}
    <div
      className={`absolute -inset-0.5 rounded-2xl bg-gradient-to-br ${from} ${to} opacity-0 group-hover:opacity-60 blur transition duration-500`}
    />
    <div className="relative h-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 hover:-translate-y-1 transition-all duration-300">
      <div
        className={`inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br ${from} ${to} text-white shadow-lg mb-4 group-hover:scale-110 transition`}
      >
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{desc}</p>
    </div>
  </div>
);

// ─── BECOME INSTRUCTOR (WhatsApp CTA) ───────────────────────────────
// Простая «удобная и понятная» секция: заголовок → 3 буллета → большая
// зелёная WhatsApp-кнопка с предзаполненным текстом сообщения. Номер
// зашит как константа здесь (а не в .env) — публичный, никакого
// секрета. Если поменяется — правим тут одно место.
const WHATSAPP_NUMBER = '77756199396'; // +7 775 619 93 96 без + и пробелов
const buildWhatsappLink = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

// Официальный логотип WhatsApp — lucide-react не содержит его, поэтому
// рисуем inline SVG (упрощённая версия пузыря с трубкой).
const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.81 11.81 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.595 5.358l-.999 3.648 3.893-.705zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);

const BecomeInstructor = ({ t }) => (
  <section
    id="become-instructor"
    className="relative scroll-mt-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-emerald-950/30 dark:via-slate-950 dark:to-teal-950/30 py-16 sm:py-24 overflow-hidden border-y border-emerald-100 dark:border-emerald-900/40"
  >
    {/* Декоративные зелёные blob'ы — связь с WhatsApp brand color */}
    <div className="absolute -top-20 left-10 h-72 w-72 rounded-full bg-emerald-300/30 dark:bg-emerald-500/15 blur-3xl pointer-events-none animate-blob" />
    <div
      className="absolute -bottom-20 right-10 h-72 w-72 rounded-full bg-teal-300/30 dark:bg-teal-500/15 blur-3xl pointer-events-none animate-blob"
      style={{ animationDelay: '5s' }}
    />

    <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
      <Reveal>
        <div className="text-center max-w-3xl mx-auto">
          
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t('landing.teachTitle')}
          </h2>

          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-300">
            {t('landing.teachSubtitle')}
          </p>
        </div>
      </Reveal>

      {/* 3 буллета в одной строке — кратко что получаешь */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {[
          [Pencil, 'teachBullet1'],
          [BarChart3, 'teachBullet2'],
          [Wallet, 'teachBullet3'],
        ].map(([Icon, key], i) => (
          <Reveal key={key} delay={i * 80}>
            <div className="flex items-center gap-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-4 py-3 shadow-sm">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 shrink-0">
                <Icon className="w-4 h-4" />
              </span>
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {t(`landing.${key}`)}
              </span>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Большая WhatsApp-кнопка с фирменным зелёным + pulse glow */}
      <Reveal delay={300}>
        <div className="mt-10 flex flex-col items-center">
          <a
            href={buildWhatsappLink(t('landing.teachWhatsappMsg'))}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 rounded-2xl bg-[#25D366] hover:bg-[#1ebe5a] text-white px-8 py-4 text-base sm:text-lg font-bold shadow-2xl shadow-emerald-500/40 transition-all hover:-translate-y-0.5 hover:scale-[1.02]"
          >
            {/* Pulse ring под кнопкой */}
            <span className="absolute inset-0 rounded-2xl bg-[#25D366] opacity-40 group-hover:opacity-60 blur-md animate-pulse -z-10" />
            <WhatsAppIcon className="w-6 h-6" />
            {t('landing.teachWhatsapp')}
            <ArrowRight className="w-5 h-5 transition group-hover:translate-x-0.5" />
          </a>

          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            {t('landing.teachReplyNote')}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

// ─── TESTIMONIALS (отзывы студентов) ────────────────────────────────
// Раньше тут была секция «Платформа в цифрах» с фейковыми числами —
// удалили, так как у нас ещё нет реальных метрик такого масштаба.
// Вместо неё — отзывы. Сейчас пустые placeholder-карточки; юзер заменит
// контент когда соберёт реальные отзывы.
//
// Структура карточки: аватар (серый плейсхолдер) + 5 звёзд + текст-цитата
// + имя + роль. Когда придёт реальный отзыв — просто заменить значения
// в массиве PLACEHOLDERS на реальный объект.
const Testimonials = ({ t }) => (
  <section className="bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
            {t('landing.testimonialsTitle')}
          </h2>
          <p className="mt-3 text-base text-slate-600 dark:text-slate-300">
            {t('landing.testimonialsSubtitle')}
          </p>
        </div>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[1, 2, 3, 4].map((i) => (
          <Reveal key={i} delay={i * 80}>
            <TestimonialCard
              text={t(`landing.t${i}Text`)}
              author={t(`landing.t${i}Author`)}
              role={t(`landing.t${i}Role`)}
              colorIndex={i - 1}
            />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// Цветовые градиенты для аватаров — каждый отзыв со своим, чтобы карточки
// визуально отличались. Юзер заменит на реальные логотипы/фотографии.
const AVATAR_GRADIENTS = [
  'from-indigo-500 to-purple-600',
  'from-emerald-500 to-cyan-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
];

const TestimonialCard = ({ text, author, role, colorIndex }) => (
  <div className="group h-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 sm:p-6 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
    {/* Quote-иконка с цветом, синхронизированным с avatar gradient */}
    <Quote className="w-8 h-8 text-indigo-400/40 dark:text-indigo-500/30 mb-3 group-hover:scale-110 transition" />

    {/* 5 звёзд */}
    <div className="flex items-center gap-0.5 mb-3">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
      ))}
    </div>

    {/* Реальный текст отзыва */}
    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 flex-1 mb-5">
      {text}
    </p>

    {/* Footer: avatar + имя + роль. Аватар — gradient-круг с инициалом.
        Юзер заменит на реальные фотографии когда соберёт отзывы. */}
    <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
      <div
        className={`h-11 w-11 rounded-full bg-gradient-to-br ${AVATAR_GRADIENTS[colorIndex] || AVATAR_GRADIENTS[0]} flex items-center justify-center shrink-0 text-white font-bold shadow-md`}
      >
        {author?.slice(0, 1) || '?'}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
          {author}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {role}
        </div>
      </div>
    </div>
  </div>
);

// ─── reusable pieces ────────────────────────────────────────────────
const Reveal = ({ children, delay = 0 }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {children}
    </div>
  );
};

const NavLink = ({ href, children }) => (
  <a
    href={href}
    className="px-3 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
  >
    {children}
  </a>
);

const Badge = ({ color, icon: Icon, text }) => {
  const palette = {
    indigo: 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    purple: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    rose: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300',
  }[color];
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${palette}`}>
      <Icon className="w-3.5 h-3.5" />
      {text}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white max-w-3xl">
    {children}
  </h2>
);

const Section = ({ id, tone, children }) => {
  const bg =
    {
      white: 'bg-white dark:bg-slate-900',
      slate: 'bg-slate-50 dark:bg-slate-950',
      gradient:
        'bg-gradient-to-b from-slate-50 via-indigo-50/30 to-slate-50 dark:from-slate-950 dark:via-indigo-950/20 dark:to-slate-950',
      cta: 'bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600',
    }[tone] || 'bg-white dark:bg-slate-900';
  return (
    <section
      id={id}
      className={`scroll-mt-20 ${bg} ${tone === 'cta' ? 'py-20' : 'py-16 sm:py-20'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">{children}</div>
    </section>
  );
};

const FEATURE_COLORS = {
  indigo: 'bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  emerald: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400',
  rose: 'bg-rose-100 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400',
  purple: 'bg-purple-100 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400',
  blue: 'bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400',
};

const FeatureCard = ({ icon: Icon, color, title, desc, compact }) => (
  <div className="h-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 sm:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
    <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${FEATURE_COLORS[color]} mb-4`}>
      <Icon className="w-5 h-5" />
    </div>
    <h3 className={`font-bold text-slate-900 dark:text-white ${compact ? 'text-base' : 'text-lg'}`}>
      {title}
    </h3>
    <p className={`mt-2 text-slate-600 dark:text-slate-300 leading-relaxed ${compact ? 'text-xs' : 'text-sm'}`}>
      {desc}
    </p>
  </div>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
      >
        <span className="font-semibold text-slate-900 dark:text-white">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-slate-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          {a}
        </div>
      )}
    </div>
  );
};

// ─── FOOTER ─────────────────────────────────────────────────────────
// 3 колонки: бренд + слоган, навигация по секциям, контакты.
// Колонка «Документы» убрана — юзеры найдут legal в /settings.
const Footer = ({ t }) => (
  <footer className="bg-slate-900 dark:bg-slate-950 text-slate-300 border-t border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-8">
      <div>
        <div className="flex items-center mb-3">
          <img src={logo} alt="" className="h-12 w-12" />
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          {t('landing.footerTagline')}
        </p>
      </div>

      <div>
        <h4 className="font-semibold text-white mb-3 text-sm">{t('landing.footerCol1')}</h4>
        <ul className="space-y-2 text-xs">
          <li><a href="#students" className="hover:text-white">{t('landing.navStudents')}</a></li>
          <li><a href="#instructors" className="hover:text-white">{t('landing.navInstructors')}</a></li>
          <li><a href="#become-instructor" className="hover:text-white">{t('landing.navTeach')}</a></li>
          <li><a href="#faq" className="hover:text-white">{t('landing.navFaq')}</a></li>
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-white mb-3 text-sm">{t('landing.footerCol3')}</h4>
        <ul className="space-y-2 text-xs">
          <li><a href="mailto:support@uaqyt.com" className="hover:text-white">support@uaqyt.com</a></li>
          <li><a href="mailto:instructors@uaqyt.com" className="hover:text-white">instructors@uaqyt.com</a></li>
        </ul>
      </div>
    </div>

    <div className="border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-xs text-slate-500 text-center">
        {t('landing.footerCopyright', { year: new Date().getFullYear() })}
      </div>
    </div>
  </footer>
);

export default LandingPage;
